import { Navigate, Outlet, useLocation } from "react-router-dom";
import Header from "./Components/Header";
import NavBar from "./Components/NavBar";
import useAuth from "./useAuth";
import Footer from "./Components/global/Footer";

export default function AppAuth() {
  const { user } = useAuth();
  const location = useLocation();
  const pathname = location.pathname?.split("/")?.[2];

  if (!user) {
    return <Navigate to="/login" />;
  }

  return (
    <div>
      <div className="min-h-screen flex bg-gray-900 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl animate-float-slow"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl animate-float-slow delay-1000"></div>
          <div className="absolute top-1/3 left-1/4 w-40 h-40 bg-indigo-500/3 rounded-full blur-2xl animate-pulse-slow"></div>
        </div>

        {/* Sidebar */}
        <div
          className={`fixed top-0 left-0 h-screen z-40 transition-all duration-500 ease-in-out w-72`}
        >
          <div className="h-full shadow-2xl bg-gradient-to-b from-[#0F0F1A] to-[#1A1A2E] border-r border-white/10 backdrop-blur-xl relative overflow-hidden">
            {/* Sidebar Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 mask-radial-fade"></div>
            </div>

            <div className="h-full overflow-y-auto custom-scrollbar relative z-10">
              <NavBar />
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className={`flex-1 transition-all duration-500 ease-in-out ml-72`}>
          {/* Header */}
          <div className="sticky top-0 z-50">
            <Header />
          </div>

          {/* Page Content */}
          <div className="relative">
            {/* Desktop View */}
            <div className="min-h-[calc(100vh-80px)] hidden lg:block">
              {/* Content Container with Glass Effect */}
              <div className="bg-gray-900 m-4 rounded-3xl backdrop-blur-xl border border-white/20 shadow-2xl shadow-black/5 min-h-[calc(100vh-140px)]">
                <div className="p-4">
                  <Outlet />
                </div>
                {/* Footer - Conditionally Rendered */}
              </div>
              <div
                className={`-m-8 mt-4 px-6 pb-6 transition-all duration-500`}
              >
                <div className="bg-gray-900 backdrop-blur-xl border border-white/20 rounded-2xl shadow-lg">
                  <Footer />
                </div>
              </div>
            </div>

            {/* Mobile View */}
            <div className="lg:hidden h-screen flex items-center justify-center relative z-50">
              {/* Mobile Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-500/10 backdrop-blur-sm"></div>

              {/* Mobile Message Card */}
              <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-8 mx-6 border border-white/20 shadow-2xl text-center transform hover:scale-105 transition-transform duration-300">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <svg
                    className="w-10 h-10 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>

                <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-4">
                  Better on Desktop
                </h3>

                <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                  For the best experience and full functionality, please access
                  this dashboard on a desktop device.
                </p>

                <div className="flex items-center justify-center space-x-2 text-gray-500">
                  <svg
                    className="w-5 h-5 animate-bounce"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 14l-7 7m0 0l-7-7m7 7V3"
                    />
                  </svg>
                  <span className="text-sm font-medium">
                    Switch to desktop view
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Background Grid Pattern */}
        <div className="fixed inset-0 bg-grid-pattern opacity-10 pointer-events-none"></div>
      </div>
    </div>
  );
}

// Add these styles to your CSS file or Tailwind config
const styles = `
@keyframes float-slow {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(180deg); }
}

@keyframes pulse-slow {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

.animate-float-slow {
  animation: float-slow 20s ease-in-out infinite;
}

.animate-pulse-slow {
  animation: pulse-slow 4s ease-in-out infinite;
}

.bg-grid-pattern {
  background-image: 
    linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px);
  background-size: 50px 50px;
}

.mask-radial-fade {
  mask-image: radial-gradient(ellipse at center, black 40%, transparent 70%);
}

.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
`;

// Inject styles
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);
